/**
 * Timeline Playhead Component
 *
 * Vertical red/white line indicating current playback position.
 * Can be dragged to scrub through the timeline.
 *
 * During playback, reads from positionRef at 60fps and sets style.left
 * directly on the DOM element for smooth animation without React re-renders.
 */

import { useCallback, useRef, useState, useEffect } from 'react'
import { pixelsToSeconds, secondsToPixels } from './timelineUtils'

interface TimelinePlayheadProps {
  /** Current playhead position in seconds (React state, throttled during playback) */
  position: number

  /** Ref tracking playhead position at 60fps during playback */
  positionRef?: React.RefObject<number>

  /** Total duration in seconds */
  totalDuration: number

  /** Zoom level (pixels per second) */
  zoomLevel: number

  /** Height of playhead in pixels */
  height: number

  /** Callback when playhead is moved */
  onPositionChange: (position: number) => void

  /** Whether playback is active */
  isPlaying: boolean
}

export function TimelinePlayhead({
  position,
  positionRef,
  totalDuration,
  zoomLevel,
  height,
  onPositionChange,
  isPlaying,
}: TimelinePlayheadProps) {
  const [isDragging, setIsDragging] = useState(false)
  const playheadRef = useRef<HTMLDivElement>(null)
  const rafRef = useRef<number | null>(null)

  // When paused, use React state for position
  const pixelPosition = secondsToPixels(position, zoomLevel)

  // During playback, use RAF to read positionRef and update DOM directly
  useEffect(() => {
    if (!isPlaying || !positionRef) {
      // Cancel any running RAF loop when paused
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      return
    }

    const updateDom = () => {
      const el = playheadRef.current
      if (el && positionRef.current !== undefined && positionRef.current !== null) {
        const px = secondsToPixels(positionRef.current, zoomLevel)
        el.style.left = `${px}px`
      }
      rafRef.current = requestAnimationFrame(updateDom)
    }

    rafRef.current = requestAnimationFrame(updateDom)

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }
  }, [isPlaying, positionRef, zoomLevel])

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (isPlaying) return // Don't allow scrubbing while playing

      e.preventDefault()
      setIsDragging(true)

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const timeline = playheadRef.current?.parentElement
        if (!timeline) return

        const rect = timeline.getBoundingClientRect()
        const relativeX = moveEvent.clientX - rect.left + timeline.scrollLeft
        const newTime = pixelsToSeconds(relativeX, zoomLevel)
        const clampedTime = Math.max(0, Math.min(newTime, totalDuration))

        onPositionChange(clampedTime)
      }

      const handleMouseUp = () => {
        setIsDragging(false)
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [isPlaying, zoomLevel, totalDuration, onPositionChange]
  )

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (isPlaying) return

      e.stopPropagation()
    },
    [isPlaying]
  )

  return (
    <div
      ref={playheadRef}
      data-testid="timeline-playhead"
      data-position-s={position.toFixed(3)}
      className={`
        absolute top-0 z-30
        ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}
      `}
      style={{
        left: `${pixelPosition}px`,
        height: `${height}px`,
        transform: 'translateX(-1px)', // Center the line
      }}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
    >
      {/* Playhead line */}
      <div className="w-0.5 h-full bg-red-500" />

      {/* Playhead handle (top triangle) */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-full">
        <div
          className="w-0 h-0 border-l-4 border-r-4 border-t-8 border-l-transparent border-r-transparent border-t-red-500"
        />
      </div>

      {/* Playhead time tooltip */}
      {isDragging && (
        <div className="absolute top-0 left-2 -translate-y-full bg-black/90 text-foreground text-xs px-2 py-1 rounded whitespace-nowrap">
          {formatPlayheadTime(position)}
        </div>
      )}
    </div>
  )
}

function formatPlayheadTime(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  const ms = Math.floor((seconds % 1) * 100)
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`
}
